import classNames from 'classnames';
import omit from 'lodash/omit';
import { useCallback, useContext, useMemo, useState } from 'react';
import { CreatedByAiLabel } from '@/modules/common/components/CreatedByAiLabel.tsx';
import {
  IssuerAddUpdateAssetModal,
  IssuerAddUpdateAssetModalProps,
} from '@/modules/issuer-dashboard/components/portfolios/IssuerAddUpdateAssetModal.tsx';
import { IssuerPortfolioAssetFullInfoModal } from '@/modules/issuer-dashboard/components/portfolios/IssuerPortfolioAssetFullInfoModal.tsx';
import { ModalBrokerAddUpdate } from '@/modules/issuer-dashboard/components/portfolios/ModalBrokerAddUpdate.tsx';
import {
  useIssuerDeletePortfolioAssetMutation,
  useIssuerPortfolioAssetsInfiniteListQuery,
} from '@/modules/issuer-dashboard/queries/portfolios.tsx';
import {
  BodyCell,
  Button,
  ConfirmModal,
  DropdownMenu,
  DropdownMenuItem,
  FilterItem,
  ModalProviderContext,
  Select,
  Spinner,
  Table,
  TableBodyColumn,
  TableHeadColumn,
  TooltipNew,
  getSelectOptionByEnum,
} from 'ui';
import { useIsMobile } from 'ui/hooks';
import { DotsHorizontalIcon, InfoCircleIcon, WarningYellowIcon } from 'ui/icons';
import { goodFormatNumber, goodFormatNumberCustom } from 'ui/utils';
import { AssetExtended, AssetType, GetAssetListV1SelfCustodyAssetGetParams } from 'api';

const tableHeadItems: TableHeadColumn[] = [
  { title: 'Asset' },
  { title: 'Type' },
  { title: 'Industry' },
  {
    title: (
      <div className="flex items-center gap-1">
        <span>Price per share</span>
        <TooltipNew
          text="Prices of publicly traded assets were updated automatically using publicly available information. Please double-check."
          place="top"
          showDelay={200}
          hideDelay={300}
          classNameTooltip="text-xs text-center max-w-56"
        >
          <img src={InfoCircleIcon} alt="info" className="w-3.5 h-3.5 min-w-3.5 cursor-pointer" />
        </TooltipNew>
      </div>
    ),
  },
  { title: 'Quantity' },
  { title: 'Total asset value' },
  { title: 'Currency' },
  { title: '% of portfolio value' },
  { title: 'PNL' },
  { title: '' },
];

export interface PortfolioAssetsTabProps {
  portfolioId: number;
  outdatedAssetIds?: Set<number>;
}

export const PortfolioAssetsTab = ({ portfolioId, outdatedAssetIds }: PortfolioAssetsTabProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const { isMobile } = useIsMobile();
  const [filters, setFilters] = useState<GetAssetListV1SelfCustodyAssetGetParams>({
    portfolio_id: portfolioId,
  });
  const {
    fetchNextPage,
    isLoading,
    hasNextPage,
    isError,
    data: dataList,
    isFetching,
  } = useIssuerPortfolioAssetsInfiniteListQuery({
    page: 1,
    page_size: 30,
    ...filters,
  });

  const handleOpenFullInfoModal = useCallback(
    (item: AssetExtended) => {
      openModal(IssuerPortfolioAssetFullInfoModal, {
        id: item.id,
        name: item.name,
        portfolioId,
      });
    },
    [openModal, portfolioId],
  );

  const tableItems = useMemo<TableBodyColumn[]>(() => {
    if (!dataList) {
      return [];
    }

    let result: TableBodyColumn[] = [];

    dataList?.pages?.forEach(page => {
      result = result.concat(
        page.data
          // ?.filter(item => !item.is_broker)
          ?.map(item => {
            const {
              name,
              asset_type,
              industry,
              price_per_share,
              quantity,
              total_asset_value,
              pnl,
              percent_portfolio_value,
              currency,
              is_broker,
              created_with_ai,
            } = item;
            const body: BodyCell[] = [
              {
                cell: (
                  <div className="flex items-center gap-3">
                    <div className="text-blue-700">{name}</div>
                    <CreatedByAiLabel createdWithAi={created_with_ai} />
                  </div>
                ),
              },
              { cell: <div>{asset_type || '-'}</div> },
              { cell: <div>{industry || '-'}</div> },
              {
                cell: !is_broker ? (
                  <div className="flex items-center gap-1.5">
                    <span>
                      {goodFormatNumberCustom(price_per_share || 0, currency?.toUpperCase())}
                    </span>
                    {!Number(price_per_share) ? (
                      <TooltipNew
                        text="This asset has not been priced yet. To set a price, click Edit on the asset."
                        place="top-end"
                        showDelay={200}
                        hideDelay={300}
                        classNameTooltip="text-xs text-center"
                      >
                        <img src={WarningYellowIcon} alt="not priced" className="w-4 h-4 min-w-4" />
                      </TooltipNew>
                    ) : (
                      outdatedAssetIds?.has(item.id) && (
                        <TooltipNew
                          text="Price hasn't been updated in 30+ days. To update it, click Edit on the asset."
                          place="top-end"
                          showDelay={200}
                          hideDelay={300}
                          classNameTooltip="text-xs text-center"
                        >
                          <img
                            src={WarningYellowIcon}
                            alt="outdated price"
                            className="w-4 h-4 min-w-4"
                          />
                        </TooltipNew>
                      )
                    )}
                  </div>
                ) : (
                  '-'
                ),
              },
              { cell: !is_broker ? goodFormatNumber(quantity || 0) : '-' },
              {
                cell: goodFormatNumberCustom(total_asset_value || 0, currency?.toUpperCase(), 0, 2),
              },
              { cell: currency?.toUpperCase() },
              {
                cell: <div>{goodFormatNumber(percent_portfolio_value || 0)}%</div>,
              },
              {
                cell: (
                  <div
                    className={classNames('whitespace-nowrap text-nowrap', {
                      'text-green-600': (pnl || 0) > 0,
                      'text-red-600': (pnl || 0) < 0,
                    })}
                  >
                    <div>{goodFormatNumberCustom(pnl ? +pnl : 0, currency?.toUpperCase())}</div>
                  </div>
                ),
              },
              { cell: <MoreButtonAsset item={item} portfolioId={portfolioId} /> },
            ];

            return {
              cells: body,
              onClick: !is_broker ? () => handleOpenFullInfoModal(item) : undefined,
            };
          }),
      );
    });
    return result;
  }, [dataList, handleOpenFullInfoModal, portfolioId, outdatedAssetIds]);

  const handleOpenAddAssetModal = useCallback(() => {
    openModal(IssuerAddUpdateAssetModal, {
      portfolioId,
    });
  }, [openModal, portfolioId]);

  const handleChangeAssetType = useCallback(
    (assetType: AssetType | null) => {
      setFilters(filters =>
        assetType ? { ...filters, asset_type: assetType } : omit(filters, 'asset_type'),
      );
    },
    [setFilters],
  );
  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-start justify-between gap-4 w-full px-3 sm:px-6">
        <div className="flex gap-2 items-center flex-wrap w-full">
          <FilterItem
            type="custom"
            className="rounded-lg w-[300px]"
            classes={{ element: 'py-0 w-full' }}
          >
            <Select
              options={getSelectOptionByEnum<AssetType>(AssetType, false, false)}
              value={filters.asset_type || null}
              onChange={handleChangeAssetType}
              showBorderBottom={false}
              isClearable={true}
            />
          </FilterItem>

          {isFetching && <Spinner size="md" variant="primary" />}
        </div>
        <div className="w-[115px]">
          <Button className="w-auto" onClick={handleOpenAddAssetModal}>
            New asset
          </Button>
        </div>
      </div>
      <Table
        className="my-0"
        classNameHead="border-t-0"
        withRounded={isMobile}
        withBorder={true}
        withBorderX={false}
        headItems={tableHeadItems}
        items={tableItems}
        isLoading={isLoading}
        isError={isError}
        hasNextPageInfinite={hasNextPage}
        fetchNextPageInfinite={fetchNextPage}
        blockIdInfinite="scrollable"
      />
    </div>
  );
};

export interface MoreButtonAssetProps {
  item: AssetExtended;
  portfolioId: number;
}

export const MoreButtonAsset = ({ item, portfolioId }: MoreButtonAssetProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const { mutateAsync: mutateAsyncDelete } = useIssuerDeletePortfolioAssetMutation();
  const itemsAsset = useMemo<DropdownMenuItem[]>(() => {
    return [
      {
        label: 'Edit',
        onClick: () => {
          openModal<IssuerAddUpdateAssetModalProps>(IssuerAddUpdateAssetModal, {
            item,
            portfolioId: portfolioId,
          });
        },
      },
      {
        label: 'Delete',
        onClick: () => {
          openModal(ConfirmModal, {
            title: <span>Delete {item.name}</span>,
            onConfirm: async () => {
              await mutateAsyncDelete(item.id);
            },
          });
        },
      },
    ];
  }, [item, mutateAsyncDelete, openModal, portfolioId]);

  const itemsBroker = useMemo<DropdownMenuItem[]>(() => {
    return [
      {
        label: 'Edit',
        onClick: () => {
          openModal(ModalBrokerAddUpdate, { item, portfolioId });
        },
      },
      // {
      //   label: 'Delete',
      //   onClick: () => {
      //     openModal(ConfirmModal, {
      //       title: <span>Delete</span>,
      //       onConfirm: async () => {
      //         await mutateAsyncDelete(item.id);
      //       },
      //     });
      //   },
      // },
    ];
  }, [item, openModal, portfolioId]);

  return (
    <div>
      <DropdownMenu
        items={!item.is_broker ? itemsAsset : itemsBroker}
        button={
          <button className="flex items-center justify-center">
            <img
              alt="More..."
              src={DotsHorizontalIcon}
              className="cursor-pointer hover:opacity-70 min-w-[24px] min-h-[24px]"
            />
          </button>
        }
        placement="left-start"
        className="w-[180px]"
        containerClassName="max-w-6"
      />
    </div>
  );
};
